// /ledger/api/commission/6890207225395335160/freeCommissionStatus
import {
	ActivityGenZRewardRes,
	AddressModel,
	AutoCopyUserDataRes,
	GenZQQuestion,
	GenZQuestionReq,
	GenZQuestionRes,
	GroupRankingRes,
	HotActivities,
	InviteeResponse,
	InviterResponse,
	PortalWatchlistRes,
	PreEarnQueueRes,
	PromotionActivity,
	PromotionTypeEnum,
	StockRebateActivityRes,
	UserActivity2023Response,
	UserRankingRes,
	UserRankModel,
} from "@/interfaces/activityType";
import { RFResponseCommon } from "@/interfaces/common";
import log2Sentry from "@/utils/log2Sentry";
import * as http from "@/utils/request";
import rfStore from "@/utils/rfStore";
import useSWR, { mutate } from "swr";
import useSWRImmutable from "swr/immutable";

const baseUrl = process.env.NEXT_PUBLIC_BASE_API;

/**
 * 是否达到免佣金资格
 * 用户免佣状态  0-不免佣  1-免佣
 */
export function useUserRichZeroCommission() {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/ledger/api/commission/${uid}/freeCommissionStatus`;

	const { data, isLoading } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<number>>(url).then((res) => res.data),
	);

	if (data !== undefined) {
		log2Sentry("useUserRichZeroCommission", "error", {
			uid: uid,
			data: data,
		});
	}

	return {
		data: data,
		isLoading,
	};
}

/**
 * 理财排队人数
 */
export function useFinancialQueueNum() {
	const url = `${baseUrl}/promotion/api/fund/qualification`;
	const { data, isLoading, mutate } = useSWR(
		url,
		async (url) => await http.get<RFResponseCommon<number>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
		mutate,
	};
}

/**
 * 查询排队情况
 */
export function useFinancialQueueInfo() {
	const uid = rfStore.getRFState()?.userId;

	const url = `${baseUrl}/promotion/api/fund/qualification/${uid}`;
	const { data, isLoading, mutate } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<PreEarnQueueRes>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
		mutate,
	};
}

/**
 * 进入排队
 */
export async function updateFinancialQueueStatus() {
	const uid = rfStore.getRFState()?.userId;

	const url = `${baseUrl}/promotion/api/fund/qualification/${uid}`;
	return await http.put(url, undefined);
}

/**
 * 2023 年终汇总
 */
// 使用 swr
export function useSummary2023() {
	const url = `${baseUrl}/promotion/annual/summary`;
	const { data, isLoading, mutate } = useSWR(
		url,
		async (url) => await http.get<RFResponseCommon<UserActivity2023Response>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
		mutate,
	};
}

/* ------------------ 股票红包 -------------- */
/**
 * 邀请奖励
 */
export function useInviteReward() {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/redPacket/reward/inviter/${uid}`;

	const { data, isLoading, mutate } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<InviterResponse>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
		mutate,
	};
}

/**
 * 被邀请奖励
 */
export function useReceivedReward() {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/redPacket/reward/invitee/${uid}`;

	const { data, isLoading, mutate } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<InviteeResponse>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
		mutate,
	};
}

/**
 * 获取邀请码对应的邀请人数
 */
export const getInviterWithInviteCode = async (inviteCode: string) => {
	const url = `${baseUrl}/promotion/api/redPacket/invite/${inviteCode}/count`;

	try {
		const { code, message, data } = await http.get<RFResponseCommon<number>>(url);
		if (code !== 200) {
			throw Error(message);
		}
		console.log(data, "===========//--------------");
		return data;
	} catch (error) {
		console.log(url);
		console.log(error);
		throw error;
	}
};

/*
 * 股票活动信息
 */
export const getStockActivityInfo = async () => {
	const url = `${baseUrl}/promotion/api/redPacket/info`;

	try {
		const { code, message, data } = await http.get<RFResponseCommon<StockRebateActivityRes>>(url);
		if (code !== 200) {
			throw Error(message);
		}
		return data;
	} catch (error) {
		console.log(url);
		console.log(error);
		throw error;
	}
};

/**
 * @summary 热门活动投票-投票数
 * @param {string} activityKey
 */
export const useActivityVote = (activityKey: HotActivities) => {
	const url = `${baseUrl}/watchlist/api/vote/list?activityKey=${activityKey}`;
	const { data, isLoading, mutate } = useSWR(
		url,
		async () =>
			await http
				.get<
					RFResponseCommon<
						{
							count: number;
							id: number;
							activityKey: string;
							symbol: string;
							actualCount: number;
							virtualCount: number;
						}[]
					>
				>(url)
				.then((res) => res.data),
	);

	const getVoteCount = (symbol: string) => {
		if (!data || data.length === 0) return 0;
		const item = data.find((item) => item.symbol === symbol);
		if (!item) return 0;
		return item.count;
	};

	return {
		isLoading,
		data,
		getVoteCount,
		mutate,
	};
};

/**
 * @summary 热门活动投票-投票
 * @param {string} activityKey
 */
export const actualActivityVote = async (activityKey: HotActivities, symbol: string) => {
	const url = `${baseUrl}/watchlist/api/vote/actual/${activityKey}/${symbol}`;
	try {
		return await http.post(url);
	} catch (error) {}
};

/**
 * @summary 热门活动投票-投票-虚拟
 * @param {string} activityKey
 */
export const virtualActivityVote = async (activityKey: HotActivities, symbol: string) => {
	const url = `${baseUrl}/watchlist/api/vote/virtual/${activityKey}/${symbol}`;
	try {
		return await http.post(url);
	} catch (error) {}
};

/**
 * @summary 返佣配置 - 是否返佣
 */
export const checkIsRebateCommission = async (userId?: string) => {
	const url = `${baseUrl}/promotion/api/involveCommission`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<boolean>>(url, {
			headers: {
				userId: userId ?? rfStore.getRFState()?.userId ?? "0",
			} as HeadersInit,
		});
		if (code !== 200) {
			throw Error(message);
		}
		return data;
	} catch (error) {
		throw error;
	}
};

export const useRebateCommissionFlag = () => {
	const currentUserId = rfStore.getRFState()?.userId ?? "0";
	const swrKey = currentUserId ? ["rebate-commission-flag", currentUserId] : null;

	const { data, isLoading } = useSWRImmutable(swrKey, async ([, userId]) => await checkIsRebateCommission(userId), {
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
	});

	return {
		data,
		isLoading,
	};
};

/**
 * @summary 获取用户数据
 */
export const getUserData = async (uid: string) => {
	const url = `${baseUrl}/social/api/rockStar/${uid}`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<AutoCopyUserDataRes>>(url);
		if (code !== 200) throw Error(message);
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * @summary 获取自动跟单活动时间
 */
export const useActivityAutoCopyTime = () => {
	const url = "https://web-cdn.rockflow.tech/h5/config/activity-auto-copy-time.json";
	const { data, isLoading, mutate } = useSWR(url, async () => await fetch(url).then((res) => res.json()));

	return {
		data,
		isLoading,
		mutate,
	};
};

export const getAutoCopyActivitiesUsers = async () => {
	const url = `${baseUrl}/social/api/rockStar/config/userIds`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<string[]>>(url);
		if (code !== 200) throw Error(message);
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * @summary 获取 Module 下面的股单列表
 */
export function useModuleWatchlist(tagId: string) {
	const rfStoreState = rfStore.getRFState();
	if (rfStoreState) {
		rfStore.setRFState({
			...rfStoreState,
			appVersion: "6.0.1",
		});
	}

	const url = `${baseUrl}/portal/api/page/watchlists?tagId=${tagId}`;
	const { data, isLoading } = useSWR(
		tagId ? url : null,
		async (url) => await http.get<RFResponseCommon<PortalWatchlistRes[]>>(url).then((res) => res.data),
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			refreshInterval: 0,
		},
	);

	return {
		data,
		isLoading,
	};
}

/******************* 梗王 **************************/
/**
 * @summary 活动次数
 */
export const useGenZActivityCount = (promotionId: string) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/times`;

	const { data, isLoading, isValidating, mutate } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<number>>(url).then((res) => res.data),
	);

	return {
		data,
		isLoading,
		isValidating,
		mutate,
	};
};

/**
 * @summary 获取问题列表
 */
export const useGenZActivityQuestion = (promotionId: string) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/questions`;

	const { data, isLoading, mutate, isValidating } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<GenZQuestionRes>>(url).then((res) => res.data),
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			refreshInterval: 0,
		},
	);

	return {
		data,
		isLoading,
		mutate,
		isValidating,
	};
};

/**
 * @summary 提交答案
 */
export const submitGenZActivityAnswer = async (
	promotionId: string,
	payload: GenZQuestionReq[],
	qaTime: number,
	roundId: number,
) => {
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/questions`;
	try {
		const { code, message, data } = await http.post<
			{ questions: GenZQuestionReq[]; timeTakenMilliseconds: number; roundId: number },
			RFResponseCommon<number>
		>(url, {
			questions: payload,
			timeTakenMilliseconds: qaTime,
			roundId: roundId,
		});
		if (code !== 200) throw Error(message);
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * @summary 检查答案
 */
export const checkGenZActivityAnswer = async (
	promotionId: string,
	{ questionId, answer }: { questionId: number; answer: number },
) => {
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/check/${questionId}/${answer}`;
	try {
		const { code, message, data } = await http.get<RFResponseCommon<boolean>>(url);
		if (code !== 200) throw Error(message);
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * @summary 加入战队
 */
export const joinGenZActivityGroup = async (promotionId: string, groupId: string) => {
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/leaderboard/groups/${groupId}`;
	try {
		const { code, message } = await http.put<any, RFResponseCommon<any>>(url, {});
		if (code !== 200) throw Error(message);
		return true;
	} catch (error) {
		throw error;
	}
};

/**
 * @summary 查看战队
 */

export const useGroup = (promotionId: string) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/groups/joined`;

	const { data, isLoading, mutate } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<boolean>>(url).then((res) => res.data),
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			refreshInterval: 0,
		},
	);

	return {
		data,
		isLoading,
		mutate,
	};
};

/**
 * @summary 获取个人排行版
 */
export const useUserRank = (promotionId: string, limit: number = 10) => {
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/leaderboard?leaderboardType=INDIVIDUAL&limit=${limit}`;

	const { data, isLoading, mutate } = useSWR(
		url,
		async (url) => await http.get<RFResponseCommon<UserRankingRes>>(url).then((res) => res.data),
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			refreshInterval: 0,
		},
	);

	return {
		data,
		isLoading,
		mutate,
	};
};
/**
 * @summary 获取战队排行版
 */
export const useGroupRank = (promotionId: string) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/leaderboard/groups`;

	const { data, isLoading, mutate } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<GroupRankingRes>>(url).then((res) => res.data),
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			refreshInterval: 0,
		},
	);

	return {
		data,
		isLoading,
		mutate,
	};
};

/**
 * @summary 获取邀请奖励
 *
 */
export const useActivityGenZRewards = (promotionId: string) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/leaderboard/rewards`;

	const { data, isLoading } = useSWR(
		uid ? url : null,
		async (url) =>
			await http
				.get<
					RFResponseCommon<{
						promotionId: number;
						userId: number;
						rewards: ActivityGenZRewardRes[];
					}>
				>(url)
				.then((res) => res.data),
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			refreshInterval: 0,
		},
	);

	return {
		data,
		isLoading,
	};
};

/**
 * @summary 获取我的排名
 */
export const useMyRanking = (promotionId: string) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/leaderboard/myrank`;
	const { data, isLoading } = useSWR(
		uid ? url : null,
		async (url) =>
			await http
				.get<RFResponseCommon<UserRankModel>>(url, {
					headers: {
						userId: uid,
					} as HeadersInit,
				})
				.then((res) => res.data),
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			refreshInterval: 0,
		},
	);
	return {
		data,
		isLoading,
	};
};

/**
 * @summary 获取我的组排名
 */
export const useMyGroupRanking = (promotionId: string) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/promotions/${promotionId}/leaderboard/grouprank`;
	const { data, isLoading } = useSWR(
		uid ? url : null,
		async (url) =>
			await http
				.get<
					RFResponseCommon<{
						groupId: number;
						name: string;
						rounds: number;
						rank: number;
					}>
				>(url)
				.then((res) => res.data),
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			refreshInterval: 0,
		},
	);
	return {
		data,
		isLoading,
	};
};

/**
 * @summary 获取地址
 */
export const useUserAddress = () => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/users/${uid}/addresses`;

	const { data, isLoading, mutate } = useSWR(
		uid ? url : null,
		async (url) => await http.get<RFResponseCommon<AddressModel[]>>(url).then((res) => res.data),
		{
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			refreshInterval: 0,
		},
	);

	let hasAddress = false;
	if (data && data.length > 0) {
		hasAddress = true;
	}
	return {
		data,
		isLoading,
		mutate,
		hasAddress,
	};
};

/**
 * @summary 添加地址
 */
export const addUserAddress = async (address: AddressModel) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/users/${uid}/addresses`;

	try {
		const { code, message } = await http.post<AddressModel, RFResponseCommon<any>>(url, address);
		if (code !== 200) throw Error(message);
		return true;
	} catch (error) {
		throw error;
	}
};

/**
 * @summary 参与活动 -- Bobby下单
 */
export const participateBobbyActivity = async (
	promotionType: PromotionTypeEnum = PromotionTypeEnum.BOBBY_ORDER_COMMISSION_REDUCTION,
) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/promotions/${promotionType}/participation/users/${uid}`;

	try {
		const { code, message } = await http.put<any, RFResponseCommon<any>>(url, {});
		if (code !== 200) throw Error(message);
		return true;
	} catch (error) {
		throw error;
	}
};

/**
 * @summary 查询是否参与
 */
export const useParticipate = (
	promotionType: PromotionTypeEnum = PromotionTypeEnum.BOBBY_ORDER_COMMISSION_REDUCTION,
) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${baseUrl}/promotion/api/promotions/${promotionType}/participation/users/${uid}`;

	const { data, isLoading, mutate } = useSWR(
		uid ? url : null,
		async (url) =>
			await http
				.get<
					RFResponseCommon<{
						id: number;
						promotionId: number;
						userId: number;
						available: string;
						createTime: string;
						updateTime: string;
					}>
				>(url)
				.then((res) => res.data),
	);

	return {
		data,
		isLoading,
		mutate,
	};
};
/**
 * @summary 获取活动详情
 */
export const getActivityInfo = async (promotionType: PromotionTypeEnum) => {
	const url = `${baseUrl}/promotion/api/${promotionType}`;

	try {
		const { code, message, data } = await http.get<RFResponseCommon<PromotionActivity>>(url);
		if (code !== 200) throw Error(message);
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * @summary 获取活动参与人数
 */
export const useActivesCount = (promotionType = PromotionTypeEnum.BOBBY_ORDER_COMMISSION_REDUCTION) => {
	const url = `${baseUrl}/promotion/api/promotions/${promotionType}/participation`;

	const { data, isLoading, mutate } = useSWR(
		url,
		async (url) =>
			await http
				.get<
					RFResponseCommon<{
						count: number;
					}>
				>(url)
				.then((res) => res.data),
	);

	return {
		data,
		isLoading,
		mutate,
	};
};
